import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clock,
  CheckCircle2,
  FileText,
  Check,
  CircleX,
  AlertTriangle,
} from "lucide-react";
import { EventSessionAttendanceItem } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/types";
import { WaitlistDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import AttendanceNotesModal from "@/modules/eventRegistration/features/EventWaitlist/components/AttendanceNotesModal/AttendanceNotesModal";
import useAttendance from "@/modules/eventRegistration/features/EventWaitlist/hooks/useAttendance";

interface AttendanceTableProps {
  attendances: EventSessionAttendanceItem[];
  loadingAttendances: boolean;
  onRefresh?: () => void;
  updateAttendance: (
    attendanceId: number,
    data: { statusId?: number; notes?: string },
  ) => Promise<void>;
  bulkUpdateAttendanceStatus: (
    attendanceIds: number[],
    statusId: number,
  ) => Promise<void>;
  isUpdatingAttendance: boolean;
  waitlistDetails: WaitlistDetails | null;
  isSessionFinalized?: boolean;
}

export default function Attendances({
  attendances,
  loadingAttendances,
  updateAttendance,
  bulkUpdateAttendanceStatus,
  isUpdatingAttendance,
  waitlistDetails,
  isSessionFinalized,
}: AttendanceTableProps) {
  const {
    showNotesModal,
    selectedAttendance,
    selectedAttendanceIds,
    bulkStatus,
    isAllSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectAttendance,
    handleBulkStatusChange,
    handleNotesClick,
    handleSaveNotes,
    handleStatusUpdate,
    setShowNotesModal,
    setBulkStatus,
  } = useAttendance({
    attendances,
    updateAttendance,
    bulkUpdateAttendanceStatus,
    waitlistDetails,
  });
  if (loadingAttendances) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Loading attendance data...
      </div>
    );
  }

  if (attendances.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No attendance records found.
      </div>
    );
  }

  return (
    <>
      {isSessionFinalized && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-green-100 dark:bg-green-800/30 rounded-full">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Session Finalized
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                This session has been finalized. Attendance records are now
                read-only.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isSessionFinalized && hasSelectedItems && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md mb-4">
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {selectedAttendanceIds.size} selected
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-700 dark:text-blue-300">Change status to:</span>
            <Select
              onValueChange={(value) => {
                setBulkStatus(value);
                handleBulkStatusChange(value);
              }}
              value={bulkStatus}
            >
              <SelectTrigger className="w-40 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors font-medium">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
                <SelectItem value="excused">Excused</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="border rounded-md dark:text-gray-200">
        <Table>
          <TableHeader>
            <TableRow>
              {!isSessionFinalized && (
                <TableHead className="w-12">
                  <Checkbox
                    aria-label="Select all"
                    checked={isAllSelected}
                    className="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 data-[state=checked]:bg-white dark:data-[state=checked]:bg-slate-800 data-[state=checked]:border-gray-400 dark:data-[state=checked]:border-slate-500 [&_svg]:stroke-gray-700 dark:[&_svg]:stroke-gray-300"
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
              )}
              <TableHead>Attendee</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              {!isSessionFinalized && <TableHead>Actions</TableHead>}
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {attendances.map((attendance) => (
              <TableRow key={attendance.id}>
                {!isSessionFinalized && (
                  <TableCell>
                    <Checkbox
                      aria-label={`Select ${attendance.attendee?.fullName || "Unknown"}`}
                      checked={selectedAttendanceIds.has(attendance.id)}
                      className="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 data-[state=checked]:bg-white dark:data-[state=checked]:bg-slate-800 data-[state=checked]:border-gray-400 dark:data-[state=checked]:border-slate-500 [&_svg]:stroke-gray-700 dark:[&_svg]:stroke-gray-300"
                      onCheckedChange={(checked) =>
                        handleSelectAttendance(
                          attendance.id,
                          checked as boolean,
                        )
                      }
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  {attendance.attendee?.fullName || "Unknown"}
                </TableCell>
                <TableCell>{attendance.attendee?.email || "N/A"}</TableCell>
                <TableCell>
                  {attendance.status ? (
                    <Badge
                      className={
                        attendance.status.name.toLowerCase() === "pending"
                          ? "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200"
                          : attendance.status.name.toLowerCase() === "present"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200"
                            : attendance.status.name.toLowerCase() === "absent"
                              ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200"
                              : attendance.status.name.toLowerCase() ===
                                  "excused"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200"
                                : ""
                      }
                      variant="default"
                    >
                      {attendance.status.name.toLowerCase() === "pending" && (
                        <Clock className="h-3 w-3 mr-1" />
                      )}
                      {attendance.status.name.toLowerCase() === "present" && (
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                      )}
                      {attendance.status.name.toLowerCase() === "absent" && (
                        <CircleX className="h-3 w-3 mr-1" />
                      )}
                      {attendance.status.name.toLowerCase() === "excused" && (
                        <AlertTriangle className="h-3 w-3 mr-1" />
                      )}
                      {attendance.status.name}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">No status</span>
                  )}
                </TableCell>
                {!isSessionFinalized && (
                  <TableCell>
                    <div className="flex gap-1">
                      {/* Present */}
                      <button
                        className={`p-1 rounded transition-colors ${
                          attendance.status?.name.toLowerCase() === "present"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "text-gray-700 dark:text-gray-300 hover:bg-green-100 hover:text-green-800 dark:hover:bg-green-900/30 dark:hover:text-green-400"
                        }`}
                        onClick={() =>
                          handleStatusUpdate(attendance.id, "present")
                        }
                        title="Mark as Present"
                      >
                        <Check className="h-4 w-4" />
                      </button>

                      {/* Absent */}
                      <button
                        className={`p-1 rounded transition-colors ${
                          attendance.status?.name.toLowerCase() === "absent"
                            ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            : "text-gray-700 dark:text-gray-300 hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        }`}
                        onClick={() =>
                          handleStatusUpdate(attendance.id, "absent")
                        }
                        title="Mark as Absent"
                      >
                        <CircleX className="h-4 w-4" />
                      </button>

                      {/* Excused */}
                      <button
                        className={`p-1 rounded transition-colors ${
                          attendance.status?.name.toLowerCase() === "excused"
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            : "text-gray-700 dark:text-gray-300 hover:bg-yellow-100 hover:text-yellow-800 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400"
                        }`}
                        onClick={() =>
                          handleStatusUpdate(attendance.id, "excused")
                        }
                        title="Mark as Excused"
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </button>

                      {/* Pending */}
                      <button
                        className={`p-1 rounded transition-colors ${
                          attendance.status?.name.toLowerCase() === "pending"
                            ? "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-900/30 dark:hover:text-gray-400"
                        }`}
                        onClick={() =>
                          handleStatusUpdate(attendance.id, "pending")
                        }
                        title="Mark as Pending"
                      >
                        <Clock className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <button
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    onClick={() => handleNotesClick(attendance)}
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AttendanceNotesModal
        attendeeName={selectedAttendance?.attendee?.fullName || "Unknown"}
        isLoading={isUpdatingAttendance}
        isOpen={showNotesModal}
        notes={selectedAttendance?.notes || null}
        onClose={() => setShowNotesModal(false)}
        onSave={handleSaveNotes}
      />
    </>
  );
}
